import type { Toast } from '@/types/toasts';
import { ref } from 'vue';
import type { RouteLocationNamedRaw } from 'vue-router';

const DEFAULT_TOAST_TIMEOUT = 5000;
const DEFAULT_MAX_TOASTS = 5;
const DEFAULT_GENERIC_ERROR: Omit<Toast, 'uuid' | 'type'> = {
	title: '',
	description: '',
};

let toastTimeout = DEFAULT_TOAST_TIMEOUT;
let maxToasts = DEFAULT_MAX_TOASTS;
let genericError = DEFAULT_GENERIC_ERROR;

export const linkIsRouteLocation = (link: string | RouteLocationNamedRaw | undefined): link is RouteLocationNamedRaw => {
	return !!link && typeof link !== 'string';
};

export const toasts = ref<Toast[]>([]);

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

export function configureToasts(config: {
	toastTimeout?: number;
	maxToasts?: number;
	genericError?: Omit<Toast, 'uuid' | 'type'>;
}) {
	toastTimeout = config.toastTimeout ?? DEFAULT_TOAST_TIMEOUT;
	maxToasts = config.maxToasts ?? DEFAULT_MAX_TOASTS;
	genericError = config.genericError ?? DEFAULT_GENERIC_ERROR;
}

export function addToast(toast: Omit<Toast, 'uuid'>, timeout: number = toastTimeout) {
	const _toast: Toast = {
		...toast,
		uuid: crypto.randomUUID(),
	};

	if (toasts.value.length >= maxToasts) {
		removeToast(toasts.value[0].uuid);
	}

	toasts.value.push(_toast);

	const timerId = setTimeout(() => {
		removeToast(_toast.uuid);
	}, timeout);
	toastTimeouts.set(_toast.uuid, timerId);
};

export function removeToast(uuid: string) {
	const timerId = toastTimeouts.get(uuid);
	if (timerId) {
		clearTimeout(timerId);
		toastTimeouts.delete(uuid);
	}

	const index = toasts.value.findIndex((toast) => toast.uuid === uuid);
	if (index !== -1) {
		toasts.value.splice(index, 1);
	}
}

export function getDefaultErrorToast(): Omit<Toast, 'uuid'> {
	return {
		type: 'error',
		...genericError,
	};
}

export function addGenericErrorToast() {
	addToast(getDefaultErrorToast());
}
